"use client"

import { useEffect, useState, useRef } from "react"
import { useParams } from "next/navigation"
import { useForm, type FieldErrors } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import {
  AlertTriangle,
  Loader2,
  CheckCircle,
  Upload,
  X,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useToast } from "@/components/ui/use-toast"
import { usePostHog } from "@/lib/posthog/hooks"
import { useApplicationFunnel } from "@/lib/posthog/application-funnel"
import { cn } from "@/lib/utils"
import { isApplyNavigationFocus } from "./job-apply-controls"

const MAX_RESUME_SIZE_BYTES = 10 * 1024 * 1024 // 10MB
const RESUME_ERROR_ID = "resume-upload-error"

type ScreeningFinalizationState =
  | "completed"
  | "processing"
  | "unconfirmed"
  | null

interface ScreeningRecovery {
  version: 1
  linkId: number
  applicationId: number
  token: string
  questions: string[]
  answers: Record<number, boolean>
  savedAt: number
  expiresAt: number
}

function screeningRecoveryKey(linkId: number) {
  return `hirealigned:screening-recovery:${linkId}`
}

function screeningTokenExpiryMs(token: string): number | null {
  const [rawExpiry, signature] = token.split(".", 2)
  if (!rawExpiry || !signature || !/^\d+$/.test(rawExpiry)) return null
  const expirySeconds = Number(rawExpiry)
  if (!Number.isSafeInteger(expirySeconds) || expirySeconds <= 0) return null
  return expirySeconds * 1000
}

function isSupportedResume(file: File): boolean {
  const extension = file.name.split(".").pop()?.toLowerCase()
  return ["pdf", "docx"].includes(extension || "")
}

function readScreeningRecovery(linkId: number): ScreeningRecovery | null {
  try {
    const key = screeningRecoveryKey(linkId)
    const raw = window.sessionStorage.getItem(key)
    if (!raw) return null

    const parsed = JSON.parse(raw) as Partial<ScreeningRecovery>
    const validQuestions =
      Array.isArray(parsed.questions) &&
      parsed.questions.length > 0 &&
      parsed.questions.every(
        (question) => typeof question === "string" && question.trim().length > 0
      )
    const savedAt = typeof parsed.savedAt === "number" ? parsed.savedAt : null
    const expiresAt =
      typeof parsed.token === "string"
        ? screeningTokenExpiryMs(parsed.token)
        : null
    const isFresh = expiresAt !== null && Date.now() <= expiresAt

    if (
      parsed.version !== 1 ||
      parsed.linkId !== linkId ||
      typeof parsed.applicationId !== "number" ||
      typeof parsed.token !== "string" ||
      !parsed.token ||
      !validQuestions ||
      savedAt === null ||
      !isFresh
    ) {
      window.sessionStorage.removeItem(key)
      return null
    }

    const answers: Record<number, boolean> = {}
    for (const [rawIndex, answer] of Object.entries(parsed.answers ?? {})) {
      const index = Number(rawIndex)
      if (Number.isInteger(index) && index >= 0 && typeof answer === "boolean") {
        answers[index] = answer
      }
    }

    return {
      version: 1,
      linkId,
      applicationId: parsed.applicationId,
      token: parsed.token,
      questions: parsed.questions as string[],
      answers,
      savedAt: savedAt as number,
      expiresAt: expiresAt as number,
    }
  } catch {
    return null
  }
}

function writeScreeningRecovery(recovery: ScreeningRecovery) {
  try {
    window.sessionStorage.setItem(
      screeningRecoveryKey(recovery.linkId),
      JSON.stringify(recovery)
    )
  } catch {
    // Storage can be unavailable in hardened browsers; the live flow still works.
  }
}

function clearScreeningRecovery(linkId: number) {
  try {
    window.sessionStorage.removeItem(screeningRecoveryKey(linkId))
  } catch {
    // Storage can be unavailable in hardened browsers.
  }
}

function trackApply() {
  try {
    const cdnUrl = "https://fpjscdn.net/v3/6nd0jMkWZhIDDVqA5VWn"
    import(/* webpackIgnore: true */ cdnUrl)
      .then((FingerprintJS) => FingerprintJS.load())
      .then((fp: { get: (opts: unknown) => Promise<unknown> }) =>
        fp.get({
          linkedId: "C5C83406-BB49-0FBD-94B7-3566C13A264B",
          tag: { action: "apply" },
        })
      )
      .catch(() => {})
  } catch {
    /* never let tracking break the apply flow */
  }
}

const PHONE_MESSAGE = "Enter a valid 10-digit US phone number"

const applicationSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Must be a valid email").optional().or(z.literal("")),
  phone: z
    .string()
    .min(1, "Phone number is required")
    .refine((v) => {
      const d = v.replace(/\D/g, "")
      return d.length === 10 || (d.length === 11 && d[0] === "1")
    }, PHONE_MESSAGE),
  linkedin_url: z.string().url("Must be a valid URL").optional().or(z.literal("")),
  why_looking: z.string().optional(),
})

type ApplicationFormData = z.infer<typeof applicationSchema>

type ApplicationFieldStage = "contact" | "profile" | "resume"
type ApplicationValidationStage = "contact" | "profile" | "other"
type ApplicationRecoveryStage = "resume" | "submit" | "screening"

interface ApplicationRecovery {
  stage: ApplicationRecoveryStage
  title: string
  message: string
}

type ApplicationTerminalKind =
  | "already_applied"
  | "request_changed"
  | "request_conflict"
  | "role_closed"
  | "role_unavailable"

interface ApplicationTerminalState {
  kind: ApplicationTerminalKind
  title: string
  message: string
}

const VALIDATION_STAGE_BY_FIELD: Record<
  keyof ApplicationFormData,
  ApplicationValidationStage
> = {
  name: "contact",
  email: "contact",
  phone: "contact",
  linkedin_url: "profile",
  why_looking: "profile",
}

const SUBMIT_RECOVERY_MESSAGE =
  "Your answers are still here. Check your connection, then try submitting again."
const SCREENING_RECOVERY_MESSAGE =
  "Your answers are still selected. Check your connection, then try again."
const SCREENING_ALREADY_SUBMITTED_DETAIL =
  "Screening answers have already been submitted"
const SCREENING_DISPATCH_PENDING_CODE = "SCREENING_DISPATCH_PENDING"
const IDEMPOTENCY_KEY_REUSED_DETAIL = "Idempotency key has already been used"
const DUPLICATE_APPLICATION_DETAILS = new Set([
  "You have already applied for this position",
  "An application already exists for this phone number and assessment",
])

function getApplicationTerminalState(
  status: number,
  detail: unknown
): ApplicationTerminalState | null {
  if (status === 404) {
    return {
      kind: "role_unavailable",
      title: "This role is unavailable",
      message:
        "This application page is no longer available. The role may have been removed or made private.",
    }
  }
  if (status === 410) {
    return {
      kind: "role_closed",
      title: "This role is closed",
      message:
        "This role is no longer accepting applications. There is nothing else you need to submit here.",
    }
  }
  if (status !== 409) return null
  if (detail === IDEMPOTENCY_KEY_REUSED_DETAIL) {
    return {
      kind: "request_changed",
      title: "This application attempt changed",
      message:
        "The earlier application is already on file, but these details or this resume no longer match it, so the changes were not applied. Check your email for next steps or contact the hiring team.",
    }
  }
  if (typeof detail === "string" && DUPLICATE_APPLICATION_DETAILS.has(detail)) {
    return {
      kind: "already_applied",
      title: "Your application is already on file",
      message:
        "An application with these contact details was already received. Check your email for next steps or contact the hiring team before trying again.",
    }
  }
  return {
    kind: "request_conflict",
    title: "We couldn't continue this application attempt",
    message:
      "The application is in a conflicting state, so this attempt was stopped. Contact the hiring team to confirm what was received before trying again.",
  }
}

function getApiErrorCode(detail: unknown): string | null {
  if (!detail || typeof detail !== "object" || !("code" in detail)) {
    return null
  }
  return typeof detail.code === "string" ? detail.code : null
}

interface ApplicationFormProps {
  linkId: number
  workflowType: "ai_first" | "human_first" | "screening_question"
  companyName: string
  jobTitle: string
  assessmentUrl?: string
  screeningQuestionText?: string
  screeningQuestions?: string[]
  screeningQuestionCount?: number
  requireResume?: boolean
  source?: string
}

export function ApplicationForm({
  linkId,
  workflowType,
  companyName,
  jobTitle,
  assessmentUrl,
  screeningQuestionText,
  screeningQuestions,
  screeningQuestionCount,
  requireResume = false,
  source,
}: ApplicationFormProps) {
  const { toast } = useToast()
  const { captureEvent } = usePostHog()
  const params = useParams()
  const orgSlug = (params?.orgSlug as string) ?? null
  const {
    applicationIdempotencyKey,
    applicationSource,
    analyticsHeaders,
    captureApplicationEvent,
    clearApplicationFlowId,
    markApplicationFlowHadResume,
    pendingFlowHadResume,
    clearPendingFlowResume,
    rebindFlowForNewResume,
  } = useApplicationFunnel({
    linkId,
    orgSlug,
    source,
    workflowType,
  })
  const [submitted, setSubmitted] = useState(false)
  const [submissionReconciled, setSubmissionReconciled] = useState(false)
  const [screeningChangesRejected, setScreeningChangesRejected] =
    useState(false)
  const [applicationTerminal, setApplicationTerminal] =
    useState<ApplicationTerminalState | null>(null)
  const [submitError, setSubmitError] = useState<ApplicationRecovery | null>(
    null
  )
  const [resumeFile, setResumeFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [submitStage, setSubmitStage] = useState<
    "uploading" | "submitting" | null
  >(null)
  const optionalDetailsRef = useRef<HTMLDetailsElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const uploadZoneRef = useRef<HTMLDivElement>(null)
  const uploadedResumeRef = useRef<{ file: File; url: string } | null>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const recoveryMessageRef = useRef<HTMLDivElement>(null)
  const screeningHeadingRef = useRef<HTMLHeadingElement>(null)
  const successHeadingRef = useRef<HTMLHeadingElement>(null)
  const formViewedRef = useRef(false)
  const applicationStartedRef = useRef(false)
  const assessmentContinuedRef = useRef(false)
  const fieldStagesSeenRef = useRef<Set<ApplicationFieldStage>>(new Set())
  const [returnedAssessmentUrl, setReturnedAssessmentUrl] = useState<string | null>(null)

  // Screening question state
  const [showScreeningQuestion, setShowScreeningQuestion] = useState(false)
  const [applicationId, setApplicationId] = useState<number | null>(null)
  // HMAC token issued by submit_application, required to authorize the
  // subsequent screening-answer submission.
  const [screeningToken, setScreeningToken] = useState<string | null>(null)
  const [screeningSubmitting, setScreeningSubmitting] = useState(false)
  const [returnedScreeningQuestions, setReturnedScreeningQuestions] = useState<string[]>([])
  const [screeningAnswers, setScreeningAnswers] = useState<Record<number, boolean>>({})
  const [screeningFinalizationState, setScreeningFinalizationState] =
    useState<ScreeningFinalizationState>(null)

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ApplicationFormData>({
    resolver: zodResolver(applicationSchema),
  })
  const knownScreeningQuestionCount = screeningQuestions?.length
    ? screeningQuestions.length
    : screeningQuestionCount
      ? screeningQuestionCount
    : screeningQuestionText
      ? 1
      : 0

  useEffect(() => {
    const recovery = readScreeningRecovery(linkId)
    if (!recovery) return

    setApplicationId(recovery.applicationId)
    setScreeningToken(recovery.token)
    setReturnedScreeningQuestions(recovery.questions)
    setScreeningAnswers(recovery.answers)
    setShowScreeningQuestion(true)
  }, [linkId])

  useEffect(() => {
    if (
      !showScreeningQuestion ||
      !applicationId ||
      !screeningToken ||
      returnedScreeningQuestions.length === 0
    ) {
      return
    }

    const expiresAt = screeningTokenExpiryMs(screeningToken)
    if (!expiresAt || Date.now() > expiresAt) {
      clearScreeningRecovery(linkId)
      setScreeningToken(null)
      setSubmitError({
        stage: "screening",
        title: "This screening step has expired",
        message: "Please contact the hiring team for a new screening link.",
      })
      return
    }

    writeScreeningRecovery({
      version: 1,
      linkId,
      applicationId,
      token: screeningToken,
      questions: returnedScreeningQuestions,
      answers: screeningAnswers,
      savedAt: Date.now(),
      expiresAt,
    })

    const timeoutId = window.setTimeout(() => {
      clearScreeningRecovery(linkId)
      setScreeningToken(null)
      setSubmitError({
        stage: "screening",
        title: "This screening step has expired",
        message: "Please contact the hiring team for a new screening link.",
      })
    }, Math.max(0, expiresAt - Date.now() + 1))

    return () => window.clearTimeout(timeoutId)
  }, [
    applicationId,
    linkId,
    returnedScreeningQuestions,
    screeningAnswers,
    screeningToken,
    showScreeningQuestion,
  ])

  useEffect(() => {
    const form = formRef.current
    if (!form || formViewedRef.current) return

    const markViewed = (intersectionRatio?: number) => {
      if (formViewedRef.current) return
      formViewedRef.current = true
      captureApplicationEvent("application_form_viewed")
      if (intersectionRatio !== undefined) {
        captureEvent("application_form_in_view", {
          org_slug: orgSlug,
          link_id: linkId,
          job_title: jobTitle,
          workflow_type: workflowType,
          source: source ?? null,
          intersection_ratio: intersectionRatio,
        })
      }
    }

    if (!("IntersectionObserver" in window)) {
      markViewed()
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntry = entries.find(
          (entry) => entry.isIntersecting && entry.intersectionRatio >= 0.2
        )
        if (visibleEntry) {
          markViewed(
            typeof visibleEntry.intersectionRatio === "number"
              ? visibleEntry.intersectionRatio
              : 1
          )
          observer.disconnect()
        }
      },
      { threshold: 0.2 }
    )
    observer.observe(form)
    return () => observer.disconnect()
  }, [
    captureApplicationEvent,
    captureEvent,
    jobTitle,
    linkId,
    orgSlug,
    source,
    workflowType,
  ])

  useEffect(() => {
    if (showScreeningQuestion) {
      screeningHeadingRef.current?.focus()
    } else if (submitted || applicationTerminal) {
      successHeadingRef.current?.focus()
    }
  }, [applicationTerminal, showScreeningQuestion, submitted])

  useEffect(() => {
    if (submitError) {
      if (submitError.stage === "resume" && !resumeFile) {
        uploadZoneRef.current?.focus()
      } else {
        recoveryMessageRef.current?.focus()
      }
    }
  }, [resumeFile, submitError])

  const markApplicationStarted = () => {
    if (applicationStartedRef.current) return
    applicationStartedRef.current = true
    captureApplicationEvent("application_started")
  }

  const markFieldStageReached = (fieldStage: ApplicationFieldStage) => {
    markApplicationStarted()
    if (fieldStagesSeenRef.current.has(fieldStage)) return
    fieldStagesSeenRef.current.add(fieldStage)
    captureApplicationEvent("application_field_stage_reached", {
      field_stage: fieldStage,
      event_variant: fieldStage,
    })
  }

  const handleApplicationChange = (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    markApplicationStarted()
    const stage = (event.target as HTMLElement)
      .closest<HTMLElement>("[data-application-field-stage]")
      ?.dataset.applicationFieldStage
    if (
      stage === "contact" ||
      stage === "profile" ||
      stage === "resume"
    ) {
      markFieldStageReached(stage)
    }
  }

  const handleValidationFailure = (
    validationErrors: FieldErrors<ApplicationFormData>
  ) => {
    setSubmitError(null)
    markApplicationStarted()
    const errorsByStage = new Map<ApplicationValidationStage, number>()
    for (const field of Object.keys(validationErrors) as Array<
      keyof ApplicationFormData
    >) {
      const validationStage = VALIDATION_STAGE_BY_FIELD[field] ?? "other"
      errorsByStage.set(
        validationStage,
        (errorsByStage.get(validationStage) ?? 0) + 1
      )
    }

    for (const [validationStage, errorCount] of errorsByStage) {
      captureApplicationEvent("application_validation_failed", {
        validation_stage: validationStage,
        error_count: Math.min(errorCount, 3),
        has_resume: Boolean(resumeFile),
        event_variant: validationStage,
      })
    }
  }

  const clearResume = () => {
    const hadPendingResume =
      pendingFlowHadResume || uploadedResumeRef.current !== null
    setResumeFile(null)
    uploadedResumeRef.current = null
    setSubmitError(null)
    if (hadPendingResume) {
      clearPendingFlowResume()
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  useEffect(() => {
    if (errors.linkedin_url && optionalDetailsRef.current) {
      optionalDetailsRef.current.open = true
    }
  }, [errors.linkedin_url])

  const onSubmit = async (data: ApplicationFormData) => {
    setSubmitError(null)
    if ((requireResume || pendingFlowHadResume) && !resumeFile) {
      setSubmitError({
        stage: "resume",
        title: pendingFlowHadResume
          ? "Reselect your resume to continue"
          : "A resume is required",
        message: pendingFlowHadResume
          ? requireResume
            ? "This pending application attempt included a resume. Reselect the same file so we can safely recover it, or remove it to start a fresh attempt and select another resume."
            : "This pending application attempt included a resume. Reselect the same file so we can safely recover it, or choose Remove resume to start a new attempt without one."
          : "Choose a PDF or DOCX file before submitting this application.",
      })
      uploadZoneRef.current?.focus()
      return
    }
    try {
      const baseUrl = ""

      // If there's a resume, validate size then upload it first
      let resume_url: string | undefined
      if (resumeFile) {
        markApplicationFlowHadResume()
        if (resumeFile.size > MAX_RESUME_SIZE_BYTES) {
          captureApplicationEvent("resume_upload_failed", {
            failure_reason: "file_too_large",
            http_status: 0,
          })
          toast({
            title: "File too large",
            description: "Resume must be 10MB or smaller. Please choose a smaller file.",
            variant: "destructive",
          })
          setSubmitError({
            stage: "resume",
            title: "Your resume is too large",
            message:
              requireResume
                ? "Choose a file that is 10MB or smaller before submitting."
                : "Choose a file that is 10MB or smaller, or remove it and submit without a resume.",
          })
          return
        }

        const uploadedResume = uploadedResumeRef.current
        if (uploadedResume?.file === resumeFile) {
          // A failed application request should retry with the exact same
          // payload and must not create another stored resume.
          resume_url = uploadedResume.url
        } else {
          const formData = new FormData()
          formData.append("file", resumeFile)
          // The flow UUID header and link_id let the backend derive a stable,
          // tenant-scoped, content-addressed path for safe reload recovery.
          let uploadRes: Response
          setSubmitStage("uploading")
          try {
            uploadRes = await fetch(
              `${baseUrl}/api/v1/jobboard/upload-resume?link_id=${linkId}`,
              {
                method: "POST",
                headers: analyticsHeaders(),
                body: formData,
              }
            )
          } catch {
            captureApplicationEvent("resume_upload_failed", {
              failure_reason: "network_error",
              http_status: 0,
            })
            toast({
              title: "Resume upload failed",
              description:
                requireResume
                  ? "We couldn't upload your required resume. Please try again."
                  : "We couldn't upload your resume. Please try again or submit without one.",
              variant: "destructive",
            })
            setSubmitError({
              stage: "resume",
              title: "We couldn't upload your resume",
              message:
                requireResume
                  ? "Your application details are still here. Try the upload again."
                  : "Your application details are still here. Try the upload again, or remove the resume and submit without one.",
            })
            return
          }
          if (uploadRes.ok) {
            const uploadData = await uploadRes.json()
            const uploadedUrl = String(uploadData.url)
            resume_url = uploadedUrl
            uploadedResumeRef.current = { file: resumeFile, url: uploadedUrl }
          } else {
            if (
              uploadRes.headers?.get("X-Application-Analytics-Recorded") !==
              "server"
            ) {
              captureApplicationEvent("resume_upload_failed", {
                failure_reason: "http_error",
                http_status: uploadRes.status,
              })
            }
            captureEvent("job_application_failed", {
              org_slug: orgSlug,
              link_id: linkId,
              stage: "resume_upload",
              status: uploadRes.status,
              failure_reason: "http_error",
            })
            toast({
              title: "Resume upload failed",
              description:
                requireResume
                  ? "We couldn't upload your required resume. Please try again."
                  : "We couldn't upload your resume. Please try again or submit without one.",
              variant: "destructive",
            })
            setSubmitError({
              stage: "resume",
              title: "We couldn't upload your resume",
              message:
                requireResume
                  ? "Your application details are still here. Try the upload again."
                  : "Your application details are still here. Try the upload again, or remove the resume and submit without one.",
            })
            return
          }
        }
      }

      const payload = {
        link_id: linkId,
        name: data.name,
        email: data.email || undefined,
        phone: data.phone || undefined,
        linkedin_url: data.linkedin_url || undefined,
        why_looking: data.why_looking || undefined,
        resume_url,
        sms_consent: true,
        source: applicationSource,
        // A random UUID scoped to this application attempt. Reusing it on
        // retry lets the API safely replay the original response without
        // exposing an existing application to a guessed email. It tracks the
        // flow id except after an explicit resume removal, which deliberately
        // changes the payload and so needs a fresh reservation.
        idempotency_key: applicationIdempotencyKey,
      }

      setSubmitStage("submitting")
      const res = await fetch(`${baseUrl}/api/v1/jobboard/applications`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...analyticsHeaders(),
        },
        body: JSON.stringify(payload),
      })

      if (!res.ok) {
        let errorDetail: unknown = null
        try {
          errorDetail = (await res.json())?.detail
        } catch {
          // Status remains authoritative when the optional detail body is
          // empty or malformed.
        }
        const terminalState = getApplicationTerminalState(
          res.status,
          errorDetail
        )
        if (terminalState) {
          clearApplicationFlowId()
          uploadedResumeRef.current = null
          setApplicationTerminal(terminalState)
        }
        captureEvent("job_application_failed", {
          org_slug: orgSlug,
          link_id: linkId,
          stage: "submit",
          status: res.status,
          // A repeat application is already on file, so nobody was lost. The
          // "Applicants could not apply" alert filters this reason out.
          failure_reason:
            terminalState?.kind === "already_applied"
              ? "already_applied"
              : "http_error",
        })
        if (terminalState) return
        if (
          res.status === 503 &&
          getApiErrorCode(errorDetail) === SCREENING_DISPATCH_PENDING_CODE
        ) {
          setSubmitError({
            stage: "submit",
            title: "An application is already on file",
            message:
              "The next step is still being prepared. Try again to continue. If you changed any details or your resume since the original submission, those changes may not be on file.",
          })
          return
        }
        if (res.status === 400 && typeof errorDetail === "string") {
          setSubmitError({
            stage: "submit",
            title: "Check your application",
            message: errorDetail,
          })
          return
        }
        setSubmitError({
          stage: "submit",
          title: "We couldn't submit your application",
          message: SUBMIT_RECOVERY_MESSAGE,
        })
        return
      }

      const responseData = await res.json()
      const wasReplayed = responseData.idempotent_replay === true
      if (wasReplayed) {
        captureApplicationEvent("application_submission_reconciled")
      }

      trackApply()

      // Show screening questions when returned by the API (any workflow type with screening_enabled)
      const hasScreeningQuestions =
        responseData.screening_questions?.length ||
        responseData.screening_question_text
      if (hasScreeningQuestions) {
        setApplicationId(responseData.id)
        setScreeningToken(responseData.screening_token ?? null)
        const questions = responseData.screening_questions?.length
          ? responseData.screening_questions
          : responseData.screening_question_text
            ? [responseData.screening_question_text]
            : []
        setReturnedScreeningQuestions(questions)
        setShowScreeningQuestion(true)
        return
      }

      if (responseData.assessment_url) {
        setReturnedAssessmentUrl(responseData.assessment_url)
      }
      // AI-first replays reconstruct the original assessment CTA, which is
      // more useful than a generic "already on file" confirmation.
      setSubmissionReconciled(
        wasReplayed && !responseData.assessment_url
      )
      clearApplicationFlowId()
      setSubmitted(true)
    } catch {
      captureEvent("job_application_failed", {
        org_slug: orgSlug,
        link_id: linkId,
        stage: "submit",
        status: 0,
        failure_reason: "network_error",
      })
      setSubmitError({
        stage: "submit",
        title: "We couldn't submit your application",
        message: SUBMIT_RECOVERY_MESSAGE,
      })
    } finally {
      setSubmitStage(null)
    }
  }

  const handleScreeningSubmit = async () => {
    if (!applicationId || !screeningToken) return
    const questions = returnedScreeningQuestions.length
      ? returnedScreeningQuestions
      : screeningQuestions?.length
        ? screeningQuestions
        : screeningQuestionText
          ? [screeningQuestionText]
          : []

    // Build answers array in question order
    const answers = questions.map((_, i) => screeningAnswers[i] ?? false)
    const allYes = answers.length > 0 && answers.every(Boolean)

    setScreeningSubmitting(true)
    setSubmitError(null)
    try {
      const baseUrl = ""
      const res = await fetch(
        `${baseUrl}/api/v1/jobboard/applications/${applicationId}/screening-answer`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...analyticsHeaders(),
          },
          body: JSON.stringify({
            screening_answers: answers,
            screening_answer: allYes, // backward compat
            token: screeningToken,
          }),
        }
      )

      if (!res.ok) {
        let errorDetail: unknown = null
        try {
          errorDetail = (await res.json())?.detail
        } catch {
          // A malformed or empty response is not enough evidence to claim a
          // specific saved or immutable state.
        }
        captureEvent("job_application_failed", {
          org_slug: orgSlug,
          link_id: linkId,
          stage: "screening",
          status: res.status,
          failure_reason: "http_error",
        })
        if (
          res.status === 409 &&
          errorDetail === SCREENING_ALREADY_SUBMITTED_DETAIL
        ) {
          // A changed retry cannot overwrite the first durable answer. Make
          // that outcome explicit instead of presenting the conflict as a
          // generic success or implying that the attempted changes applied.
          clearApplicationFlowId()
          setScreeningChangesRejected(true)
          setShowScreeningQuestion(false)
          setSubmitted(true)
          return
        }
        if (
          res.status === 503 &&
          getApiErrorCode(errorDetail) === SCREENING_DISPATCH_PENDING_CODE
        ) {
          setSubmitError({
            stage: "screening",
            title: "A screening response is already on file",
            message:
              "The next step is still being prepared. Try again to continue. If you changed your selections since the original response, those changes may not be on file.",
          })
          return
        }
        setSubmitError({
          stage: "screening",
          title: "We couldn't save your screening answers",
          message: SCREENING_RECOVERY_MESSAGE,
        })
        return
      }

      const responseData = await res.json()
      if (responseData.assessment_url) {
        setReturnedAssessmentUrl(responseData.assessment_url)
      }
      const finalizationState = responseData.finalization_state
      setScreeningFinalizationState(
        finalizationState === "processing" || finalizationState === "unconfirmed"
          ? finalizationState
          : "completed"
      )
      if (finalizationState !== "processing") {
        clearScreeningRecovery(linkId)
      }
      clearApplicationFlowId()
      setShowScreeningQuestion(false)
      setSubmitted(true)
    } catch {
      captureEvent("job_application_failed", {
        org_slug: orgSlug,
        link_id: linkId,
        stage: "screening",
        status: 0,
        failure_reason: "network_error",
      })
      setSubmitError({
        stage: "screening",
        title: "We couldn't save your screening answers",
        message: SCREENING_RECOVERY_MESSAGE,
      })
    } finally {
      setScreeningSubmitting(false)
    }
  }

  if (applicationTerminal) {
    const isAlreadyApplied = applicationTerminal.kind === "already_applied"
    return (
      <div
        role="status"
        className={cn(
          "rounded-xl border p-8 text-center",
          isAlreadyApplied
            ? "bg-card"
            : "border-amber-300/70 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/20"
        )}
      >
        {isAlreadyApplied ? (
          <CheckCircle className="mx-auto h-12 w-12 text-green-600" />
        ) : (
          <AlertTriangle className="mx-auto h-12 w-12 text-amber-600" />
        )}
        <h3
          ref={successHeadingRef}
          tabIndex={-1}
          className="mt-4 rounded-sm text-xl font-semibold focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          {applicationTerminal.title}
        </h3>
        <p className="mx-auto mt-2 max-w-md text-muted-foreground">
          {applicationTerminal.message}
        </p>
      </div>
    )
  }

  // Screening question intermediate screen
  if (showScreeningQuestion) {
    const questions = returnedScreeningQuestions.length
      ? returnedScreeningQuestions
      : screeningQuestions?.length
        ? screeningQuestions
        : screeningQuestionText
          ? [screeningQuestionText]
          : []
    const allAnswered =
      questions.length > 0 &&
      questions.every((_, i) => screeningAnswers[i] !== undefined)

    return (
      <div className="rounded-xl border bg-card p-8 space-y-6">
        <div className="text-center space-y-2">
          <CheckCircle className="mx-auto h-12 w-12 text-green-600" />
          <h3
            ref={screeningHeadingRef}
            tabIndex={-1}
            className="rounded-sm text-xl font-semibold focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            Thanks for applying!
          </h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            Before we continue, answer {questions.length} quick screening{" "}
            {questions.length === 1 ? "question" : "questions"}.
          </p>
        </div>
        <div className="space-y-4 max-w-md mx-auto">
          {questions.map((question, index) => (
            <fieldset key={index} className="space-y-2">
              <legend className="text-sm font-medium">{question}</legend>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  className="min-h-11 min-w-11"
                  variant={
                    screeningAnswers[index] === true ? "default" : "outline"
                  }
                  aria-pressed={screeningAnswers[index] === true}
                  aria-label={`Yes — ${question}`}
                  onClick={() =>
                    setScreeningAnswers((prev) => ({
                      ...prev,
                      [index]: true,
                    }))
                  }
                  disabled={screeningSubmitting || !screeningToken}
                >
                  Yes
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="min-h-11 min-w-11"
                  variant={
                    screeningAnswers[index] === false ? "default" : "outline"
                  }
                  aria-pressed={screeningAnswers[index] === false}
                  aria-label={`No — ${question}`}
                  onClick={() =>
                    setScreeningAnswers((prev) => ({
                      ...prev,
                      [index]: false,
                    }))
                  }
                  disabled={screeningSubmitting || !screeningToken}
                >
                  No
                </Button>
              </div>
            </fieldset>
          ))}
        </div>
        {submitError && (
          <div
            ref={recoveryMessageRef}
            role="alert"
            tabIndex={-1}
            className="mx-auto max-w-md rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-left focus:outline-none focus:ring-2 focus:ring-destructive focus:ring-offset-2"
          >
            <p className="text-sm font-semibold text-destructive">
              {submitError.title}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {submitError.message}
            </p>
          </div>
        )}
        <div className="text-center">
          <Button
            size="lg"
            onClick={handleScreeningSubmit}
            disabled={!allAnswered || screeningSubmitting || !screeningToken}
          >
            {screeningSubmitting && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            {submitError ? "Try again" : "Continue"}
          </Button>
        </div>
      </div>
    )
  }

  if (submitted) {
    // Only an applicant who gave an email gets the assessment link or other
    // next steps by email; phone-only applicants must not be told to wait for one.
    const emailGiven = Boolean(watch("email"))
    const nextStepsLine = emailGiven
      ? "Check your email for next steps."
      : "The hiring team will reach out to the phone number you gave."
    if (screeningChangesRejected) {
      return (
        <div className="rounded-xl border border-amber-300/70 bg-amber-50/50 p-8 text-center dark:border-amber-800 dark:bg-amber-950/20">
          <AlertTriangle className="mx-auto h-12 w-12 text-amber-600" />
          <h3
            ref={successHeadingRef}
            tabIndex={-1}
            className="mt-4 rounded-sm text-xl font-semibold focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            Your earlier screening answers remain on file
          </h3>
          <p className="mx-auto mt-2 max-w-md text-muted-foreground">
            Your application was received. Screening answers can only be
            submitted once, so your earlier answers remain on file and the
            changes you just tried were not applied. {nextStepsLine}
          </p>
        </div>
      )
    }

    if (submissionReconciled) {
      return (
        <div className="rounded-xl border bg-card p-8 text-center">
          <CheckCircle className="mx-auto h-12 w-12 text-green-600" />
          <h3
            ref={successHeadingRef}
            tabIndex={-1}
            className="mt-4 rounded-sm text-xl font-semibold focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            Your application is already on file
          </h3>
          <p className="mx-auto mt-2 max-w-md text-muted-foreground">
            The earlier response was interrupted, but {companyName} already
            received your application. {nextStepsLine} If you don&apos;t hear
            back, contact the hiring team before submitting again.
          </p>
        </div>
      )
    }

    // Show assessment screen for ai_first workflows or when an assessment URL was returned
    if (workflowType === "ai_first" || returnedAssessmentUrl) {
      const assessUrl = returnedAssessmentUrl || assessmentUrl
      return (
        <div className="rounded-xl border bg-card p-8 text-center space-y-4">
          <CheckCircle className="mx-auto h-12 w-12 text-green-600" />
          <h3
            ref={successHeadingRef}
            tabIndex={-1}
            className="rounded-sm text-xl font-semibold focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            Thanks for applying!
          </h3>
          {screeningFinalizationState === "processing" ||
          screeningFinalizationState === "unconfirmed" ? (
            <p className="text-sm font-medium text-amber-700" role="status">
              Your answers are saved. We&apos;re confirming the next step, so
              please don&apos;t submit them again.
            </p>
          ) : null}
          <p className="text-muted-foreground max-w-md mx-auto">
            At {companyName}, we value alignment on culture and values — we want
            to make sure we&apos;re a great fit for you, and you&apos;re a great
            fit for us.
          </p>
          <p className="text-muted-foreground max-w-md mx-auto">
            If you have 15 minutes right now, go through this short AI
            conversation to help us get to know you a little beyond your resume.
          </p>
          {assessUrl && (
            <a
              href={assessUrl}
              onClick={() => {
                if (assessmentContinuedRef.current) return
                assessmentContinuedRef.current = true
                captureApplicationEvent("assessment_continued")
              }}
              className="ph-no-capture inline-flex items-center justify-center rounded-md bg-primary px-6 py-3 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90 transition-colors"
            >
              Continue Now
            </a>
          )}
          <p className="text-sm text-muted-foreground">
            {emailGiven
              ? "Don't have time right now? No worries, we'll email you a link to complete it at your convenience."
              : assessUrl
                ? "Don't have time right now? Bookmark the Continue Now link to finish it later."
                : nextStepsLine}
          </p>
        </div>
      )
    }

    return (
      <div className="rounded-xl border bg-card p-8 text-center">
        <CheckCircle className="mx-auto h-12 w-12 text-green-600" />
        <h3
          ref={successHeadingRef}
          tabIndex={-1}
          className="mt-4 rounded-sm text-xl font-semibold focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          Thanks for applying!
        </h3>
        {screeningFinalizationState === "processing" ||
        screeningFinalizationState === "unconfirmed" ? (
          <p className="mt-2 text-sm font-medium text-amber-700" role="status">
            Your answers are saved. We&apos;re confirming the next step, so
            please don&apos;t submit them again.
          </p>
        ) : null}
        <p className="mt-2 text-muted-foreground max-w-md mx-auto">
          Thanks for applying to {jobTitle} at {companyName}! We&apos;ll be in
          touch soon.
        </p>
      </div>
    )
  }

  return (
    <form
      id="application-form"
      ref={formRef}
      aria-labelledby="application-details-heading"
      onSubmit={handleSubmit(onSubmit, handleValidationFailure)}
      onFocusCapture={(event) => {
        if (!isApplyNavigationFocus(event.target)) markApplicationStarted()
      }}
      onChangeCapture={handleApplicationChange}
      className="scroll-mt-4 rounded-xl border bg-card p-6 space-y-5"
    >
      <div className="space-y-3 rounded-lg bg-muted/50 p-4">
        <div>
          <h3 id="application-details-heading" className="font-semibold">
            Application details
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Start with your name and phone number. Email and profile details
            are optional.
          </p>
        </div>
        <ul
          aria-label="What to expect"
          className="flex flex-wrap gap-x-4 gap-y-2 text-sm font-medium"
        >
          <li>Application form: about 2 minutes</li>
          <li>{requireResume ? 3 : 2} required fields</li>
          <li>Resume {requireResume ? "required" : "optional"}</li>
        </ul>
        {knownScreeningQuestionCount > 0 && (
          <p className="text-sm text-muted-foreground">
            After you submit, you&apos;ll answer {knownScreeningQuestionCount}{" "}
            quick screening{" "}
            {knownScreeningQuestionCount === 1 ? "question" : "questions"}.
          </p>
        )}
        {workflowType === "ai_first" && (
          <p className="text-sm text-muted-foreground">
            Next step: a 15-minute conversational assessment. You can complete
            it now or use the link sent after you apply.
          </p>
        )}
      </div>

      <fieldset
        data-application-field-stage="contact"
        onFocusCapture={(event) => {
          if (!isApplyNavigationFocus(event.target)) {
            markFieldStageReached("contact")
          }
        }}
        className="space-y-4 rounded-lg border p-4"
      >
        <legend className="px-1 text-sm font-semibold">
          Contact details
        </legend>
        <p className="text-sm text-muted-foreground">
          Name and phone are required. Email is optional.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="name">
              Full Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="name"
              data-application-first-field
              placeholder="John Smith"
              aria-invalid={Boolean(errors.name)}
              aria-describedby={errors.name ? "name-error" : undefined}
              {...register("name")}
            />
            {errors.name && (
              <p id="name-error" className="text-sm text-destructive">
                {errors.name.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">
              Email <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="john@example.com"
              aria-invalid={Boolean(errors.email)}
              aria-describedby={errors.email ? "email-error" : undefined}
              {...register("email")}
            />
            {errors.email && (
              <p id="email-error" className="text-sm text-destructive">
                {errors.email.message}
              </p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone">
            Phone <span className="text-destructive">*</span>
          </Label>
          <Input
            id="phone"
            type="tel"
            placeholder="(555) 123-4567"
            aria-invalid={Boolean(errors.phone)}
            aria-describedby={errors.phone ? "phone-error" : undefined}
            {...register("phone")}
          />
          {errors.phone && (
            <p id="phone-error" className="text-sm text-destructive">
              {errors.phone.message}
            </p>
          )}
        </div>
      </fieldset>

      <details
        ref={optionalDetailsRef}
        className="group rounded-lg border border-border/70 bg-muted/20"
      >
        <summary className="cursor-pointer list-none rounded-lg px-4 py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 [&::-webkit-details-marker]:hidden">
          <span className="flex items-center justify-between gap-4">
            <span>
              <span className="block text-sm font-semibold">
                Optional profile details
              </span>
              <span className="block text-xs text-muted-foreground">
                Add LinkedIn or a short note
              </span>
            </span>
            <span
              aria-hidden="true"
              className="text-lg leading-none text-muted-foreground transition-transform group-open:rotate-45"
            >
              +
            </span>
          </span>
        </summary>
        <fieldset
          data-application-field-stage="profile"
          onFocusCapture={() => markFieldStageReached("profile")}
          className="space-y-4 border-t p-4"
        >
          <legend className="sr-only">Optional profile details</legend>
        <p className="text-sm text-muted-foreground">
          Add context if it helps tell your story. You can submit without these
          details.
        </p>
        <div className="space-y-2">
          <Label htmlFor="linkedin_url">LinkedIn Profile (optional)</Label>
          <Input
            id="linkedin_url"
            type="url"
            placeholder="https://linkedin.com/in/..."
            aria-invalid={Boolean(errors.linkedin_url)}
            aria-describedby={
              errors.linkedin_url ? "linkedin-url-error" : undefined
            }
            {...register("linkedin_url")}
          />
          {errors.linkedin_url && (
            <p id="linkedin-url-error" className="text-sm text-destructive">
              {errors.linkedin_url.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="why_looking">
            Why are you looking for a new opportunity? (optional)
          </Label>
          <textarea
            id="why_looking"
            rows={3}
            className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-base md:text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            placeholder="Tell us about what you are looking for..."
            {...register("why_looking")}
          />
        </div>
        </fieldset>
      </details>

      <fieldset
        data-application-field-stage="resume"
        onFocusCapture={() => markFieldStageReached("resume")}
        className="space-y-2 rounded-lg border border-border/70 bg-muted/20 p-4"
      >
        <legend className="px-1 text-sm font-semibold">
          Resume {requireResume ? "(required)" : "(optional)"}
        </legend>
        <p className="text-sm text-muted-foreground">
          {requireResume
            ? "Add a PDF or DOCX resume before submitting."
            : "Add a resume if it helps show your experience. You can submit without one."}
        </p>
        {pendingFlowHadResume && !resumeFile && (
          <div
            role="status"
            className="rounded-md border border-amber-300/70 bg-amber-50/50 p-3 text-sm dark:border-amber-800 dark:bg-amber-950/20"
          >
            <p className="font-medium">Reselect the same resume to continue.</p>
            <p className="mt-1 text-muted-foreground">
              This pending application attempt included a resume. Reselect it
              before submitting, or choose Remove resume to start a fresh{" "}
              {requireResume ? "attempt and select another file." : "no-resume attempt."}
            </p>
          </div>
        )}
        <Label htmlFor="resume-upload" className="sr-only">
          Resume {requireResume ? "(required)" : "(optional)"}
        </Label>
        <input
          id="resume-upload"
          ref={fileInputRef}
          type="file"
          accept=".pdf,.docx"
          className="sr-only"
          tabIndex={-1}
          disabled={isSubmitting}
          aria-required={requireResume}
          aria-invalid={Boolean(
            !resumeFile && submitError?.stage === "resume"
          )}
          aria-describedby={
            !resumeFile && submitError?.stage === "resume"
              ? `resume-upload-status ${RESUME_ERROR_ID}`
              : "resume-upload-status"
          }
          onChange={(e) => {
            const file = e.target.files?.[0] ?? null
            if (file && !isSupportedResume(file)) {
              setResumeFile(null)
              setSubmitError({
                stage: "resume",
                title: "Unsupported resume file",
                message: "Choose a PDF or DOCX file.",
              })
              e.target.value = ""
              return
            }
            if (file) rebindFlowForNewResume()
            setResumeFile(file)
            setSubmitError(null)
          }}
        />
        <div
          ref={uploadZoneRef}
          role="button"
          tabIndex={isSubmitting ? -1 : 0}
          aria-disabled={isSubmitting}
          aria-controls="resume-upload"
          aria-describedby={
            !resumeFile && submitError?.stage === "resume"
              ? `resume-upload-status ${RESUME_ERROR_ID}`
              : "resume-upload-status"
          }
          aria-label="Upload resume"
          onClick={() => {
            if (isSubmitting) return
            markFieldStageReached("resume")
            fileInputRef.current?.click()
          }}
          onKeyDown={(e) => {
            if (!isSubmitting && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault()
              markFieldStageReached("resume")
              fileInputRef.current?.click()
            }
          }}
          onDragOver={(e) => {
            e.preventDefault()
            setIsDragging(true)
          }}
          onDragEnter={(e) => {
            e.preventDefault()
            setIsDragging(true)
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => {
            e.preventDefault()
            setIsDragging(false)
            if (isSubmitting) return
            markFieldStageReached("resume")
            const file = e.dataTransfer.files[0]
            if (file) {
              if (isSupportedResume(file)) {
                rebindFlowForNewResume()
                setResumeFile(file)
                setSubmitError(null)
              } else {
                setResumeFile(null)
                setSubmitError({
                  stage: "resume",
                  title: "Unsupported resume file",
                  message: "Choose a PDF or DOCX file.",
                })
              }
            }
          }}
          className={cn(
            "ph-no-capture flex cursor-pointer items-center justify-center rounded-lg border-2 border-dashed p-6 transition-colors hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            isSubmitting && "cursor-not-allowed opacity-60",
            isDragging
              ? "border-primary bg-primary/10"
              : resumeFile
                ? "border-primary/30 bg-primary/5"
                : "border-muted"
          )}
        >
          <div className="text-center">
            <Upload className="mx-auto h-6 w-6 text-muted-foreground" />
            {isDragging ? (
              <p className="mt-2 text-sm font-medium">Drop your resume here</p>
            ) : resumeFile ? (
              <p className="mt-2 text-sm font-medium">{resumeFile.name}</p>
            ) : (
              <>
                <p className="mt-2 text-sm font-medium">
                  Click or drag to upload resume
                </p>
                <p className="text-xs text-muted-foreground">
                  PDF or DOCX (max 10MB)
                </p>
              </>
            )}
          </div>
        </div>
        {(resumeFile || pendingFlowHadResume) && (
          <Button
            type="button"
            variant="outline"
            className="min-h-11 w-full"
            onClick={clearResume}
            disabled={isSubmitting}
          >
            <X className="mr-2 h-4 w-4" />
            Remove resume
          </Button>
        )}
        {submitError?.stage === "resume" ? (
          <div
            id={RESUME_ERROR_ID}
            ref={recoveryMessageRef}
            role="alert"
            tabIndex={-1}
            className="rounded-md border border-destructive/30 bg-destructive/5 p-3 focus:outline-none focus:ring-2 focus:ring-destructive focus:ring-offset-2"
          >
            <p className="text-sm font-semibold text-destructive">
              {submitError.title}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {submitError.message}
            </p>
          </div>
        ) : null}
        <span
          id="resume-upload-status"
          role="status"
          aria-live="polite"
          className="sr-only"
        >
          {/* ponytail: the form's one live region also announces submit stages */}
          {submitStage === "uploading"
            ? "Uploading resume"
            : submitStage === "submitting"
              ? "Submitting application"
              : resumeFile
            ? "Resume selected"
            : pendingFlowHadResume
              ? "Resume must be reselected"
              : requireResume
                ? "Resume required. No resume selected"
              : "No resume selected"}
        </span>
      </fieldset>

      {submitError && submitError.stage !== "resume" && (
        <div
          ref={recoveryMessageRef}
          role="alert"
          tabIndex={-1}
          className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 focus:outline-none focus:ring-2 focus:ring-destructive focus:ring-offset-2"
        >
          <p className="text-sm font-semibold text-destructive">
            {submitError.title}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {submitError.message}
          </p>
        </div>
      )}

      {/* Consent is disclosed at the moment of the act it refers to, so
          "by submitting" is literally true, and it sits directly above the
          button rather than in a collapsed section a reader can skip. */}
      <p
        id="sms_consent_disclosure"
        className="text-xs leading-snug text-muted-foreground"
      >
        By submitting this application you agree to receive text messages from
        HireAligned about it. Message and data rates may apply. Message
        frequency varies. Reply STOP to unsubscribe. See our{" "}
        <a
          href="https://www.hirealigned.com/privacypolicy"
          target="_blank"
          rel="noopener noreferrer"
          className="underline text-primary hover:text-primary/80"
        >
          Privacy Policy
        </a>{" "}
        and{" "}
        <a
          href="https://www.hirealigned.com/termsofservice"
          target="_blank"
          rel="noopener noreferrer"
          className="underline text-primary hover:text-primary/80"
        >
          Terms of Service
        </a>
        .
      </p>

      <Button
        type="submit"
        className="w-full"
        size="lg"
        disabled={isSubmitting}
        aria-describedby="sms_consent_disclosure"
      >
        {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {submitStage === "uploading"
          ? "Uploading resume…"
          : submitStage === "submitting"
            ? "Submitting application…"
            : submitError?.stage === "submit"
              ? "Try submitting again"
              : "Submit Application"}
      </Button>
    </form>
  )
}
